/**
 * LES BROUILLONS, UN PAR CONVERSATION : ce qu'on a commence a ecrire dans une
 * conversation y reste quand on passe a une autre, survit a un rechargement,
 * et s'efface a l'envoi. Pur, sans React : teste par
 * `packages/ui/tests/brouillons.test.mjs`.
 *
 * - CLE : une conversation (`conversation:<id>`), un fil de cette conversation
 *   (`conversation:<id>:fil:<parentId>`), un sujet de Lia (`lia:<id>`). Une cle
 *   `memoire:` est un brouillon sans conversation identifiable : il vit en
 *   memoire, jamais sur le poste, jamais dans une liste.
 * - STOCKAGE : le `localStorage` du poste, une entree par brouillon, rangee
 *   sous l'organisation ET la personne (`taki:brouillon:<orgId>:<uid>:<cle>`) :
 *   deux personnes du meme poste ne se voient jamais.
 * - MEMOIRE : tout se lit en memoire, de facon synchrone ; le stockage n'est lu
 *   qu'une fois, a la creation. S'il jette (quota, navigation privee), la
 *   memoire continue seule.
 */

/** Un brouillon oublie depuis plus de 30 jours est jete a la creation du magasin. */
export const DUREE_DE_VIE_DES_BROUILLONS_MS = 30 * 24 * 60 * 60 * 1000;

const PREFIXE_MEMOIRE = 'memoire:';

/** La cle du brouillon d'une conversation du socle, ou de son fil quand `parentId` est donne. */
export function cleDeConversation(conversationId: string, parentId?: string | null): string {
  return parentId ? `conversation:${conversationId}:fil:${parentId}` : `conversation:${conversationId}`;
}

/** La cle du brouillon d'un sujet de Lia. */
export function cleDeSujetLia(sujetId: string): string {
  return `lia:${sujetId}`;
}

/**
 * Cette conversation a-t-elle un brouillon ? Vrai si sa cle est dans
 * l'ensemble, ou si une cle en descend (`<cle>:fil:<parentId>`) : un fil en
 * cours marque sa conversation. Le test est sur `${cle}:`, jamais sur `cle`
 * seule : `conversation:ab` n'est pas le debut de `conversation:abc`.
 */
export function aUnBrouillon(cles: ReadonlySet<string>, cle: string): boolean {
  if (cles.has(cle)) return true;
  const debut = `${cle}:`;
  for (const autre of cles) if (autre.startsWith(debut)) return true;
  return false;
}

export interface MagasinDeBrouillons {
  /** Le brouillon de cette cle ; `''` s'il n'y en a pas. Ne touche jamais le stockage. */
  lire(cle: string): string;
  /** Pose le brouillon ; un texte blanc retire l'entree du stockage. */
  ecrire(cle: string, texte: string): void;
  /** Appele a chaque changement ; rend la fonction qui se desabonne. */
  ecouter(rappel: () => void): () => void;
  /** Les cles qui ont un brouillon non blanc, `memoire:` exclues. Meme reference tant que rien ne change. */
  cles(): ReadonlySet<string>;
  /** Un evenement `storage` d'un autre onglet : `cleDeStockage` `null` veut dire « tout vide ». */
  surStockageExterne(cleDeStockage: string | null, valeur: string | null): void;
}

interface EntreeStockee { texte: string; le: number }

/** Lit une entree du stockage ; `null` si elle est invalide, vide ou trop vieille. */
function lireEntree(brut: string | null, maintenant: number): EntreeStockee | null {
  if (brut === null) return null;
  try {
    const valeur: unknown = JSON.parse(brut);
    if (valeur === null || typeof valeur !== 'object') return null;
    const { texte, le } = valeur as { texte?: unknown; le?: unknown };
    if (typeof texte !== 'string' || typeof le !== 'number' || !Number.isFinite(le)) return null;
    if (texte.trim() === '' || maintenant - le > DUREE_DE_VIE_DES_BROUILLONS_MS) return null;
    return { texte, le };
  } catch {
    return null;
  }
}

export function creerMagasinDeBrouillons({ orgId, uid, stockage, maintenant = Date.now }: {
  orgId: string;
  uid: string;
  stockage: Storage | null;
  maintenant?: () => number;
}): MagasinDeBrouillons {
  const prefixe = `taki:brouillon:${orgId}:${uid}:`;
  const textes = new Map<string, string>();
  const ecouteurs = new Set<() => void>();
  let ensemble: ReadonlySet<string> = new Set();

  const persistable = (cle: string): boolean => !cle.startsWith(PREFIXE_MEMOIRE);
  const essayer = (action: () => void): void => {
    try { action(); } catch { /* Stockage plein ou interdit : la memoire continue seule. */ }
  };

  // A la creation : on relit les brouillons de CETTE personne, on jette (et on retire) l'invalide et le perime.
  if (stockage !== null) {
    essayer(() => {
      const trouvees: string[] = [];
      for (let i = 0; i < stockage.length; i += 1) {
        const cleDeStockage = stockage.key(i);
        if (cleDeStockage !== null && cleDeStockage.startsWith(prefixe)) trouvees.push(cleDeStockage);
      }
      const t = maintenant();
      for (const cleDeStockage of trouvees) {
        const cle = cleDeStockage.slice(prefixe.length);
        const entree = persistable(cle) ? lireEntree(stockage.getItem(cleDeStockage), t) : null;
        if (entree === null) essayer(() => stockage.removeItem(cleDeStockage));
        else textes.set(cle, entree.texte);
      }
    });
  }

  // Le meme ensemble tant que ses cles ne bougent : `useSyncExternalStore` ne rend pas a chaque frappe.
  const recalculer = (): void => {
    const suivantes = new Set<string>();
    for (const [cle, texte] of textes) if (persistable(cle) && texte.trim() !== '') suivantes.add(cle);
    if (suivantes.size === ensemble.size && [...suivantes].every((cle) => ensemble.has(cle))) return;
    ensemble = suivantes;
  };
  recalculer();

  const notifier = (): void => { for (const rappel of [...ecouteurs]) rappel(); };

  return {
    lire: (cle) => textes.get(cle) ?? '',
    ecrire(cle, texte) {
      if ((textes.get(cle) ?? '') === texte) return;
      if (texte === '') textes.delete(cle); else textes.set(cle, texte);
      recalculer();
      if (stockage !== null && persistable(cle)) {
        const cleDeStockage = `${prefixe}${cle}`;
        if (texte.trim() === '') essayer(() => stockage.removeItem(cleDeStockage));
        else essayer(() => stockage.setItem(cleDeStockage, JSON.stringify({ texte, le: maintenant() } satisfies EntreeStockee)));
      }
      notifier();
    },
    ecouter(rappel) {
      ecouteurs.add(rappel);
      return () => { ecouteurs.delete(rappel); };
    },
    cles: () => ensemble,
    surStockageExterne(cleDeStockage, valeur) {
      let change = false;
      if (cleDeStockage === null) {
        for (const cle of [...textes.keys()]) if (persistable(cle)) { textes.delete(cle); change = true; }
      } else if (cleDeStockage.startsWith(prefixe)) {
        const cle = cleDeStockage.slice(prefixe.length);
        if (!persistable(cle)) return;
        const entree = lireEntree(valeur, maintenant());
        if ((textes.get(cle) ?? '') === (entree?.texte ?? '')) return;
        if (entree === null) textes.delete(cle); else textes.set(cle, entree.texte);
        change = true;
      }
      if (!change) return;
      recalculer();
      notifier();
    },
  };
}
